import type * as React from 'react'
import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import type { Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  toast,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import { useTypedMutation } from '../../../hooks/useTypedMutation'
import { usePostData, useTrackActionClicked } from '../PostCardContext'

// Cache-modifier view of a posts connection edge: `node` is a normalized
// cache reference read through `readField`, not the full wire Post type.
type PostsEdge = {
  node: Reference | StoreObject
}

const DeletePostNatively = graphql(/* GraphQL */ `
  mutation AdminDeleteSentPost($id: PostId!) {
    deletePost(input: { id: $id }) {
      __typename
      ... on VoidMutationError {
        message
      }
      ... on DeletePostSuccess {
        id
      }
    }
  }
`)

type UseAdminDeletePost = ReturnType<
  typeof useTypedMutation<
    'DeletePostSuccess',
    ResultOf<typeof DeletePostNatively>['deletePost'],
    ResultOf<typeof DeletePostNatively>,
    VariablesOf<typeof DeletePostNatively>
  >
>

const useAdminDeletePost = (): UseAdminDeletePost =>
  useTypedMutation(DeletePostNatively, (data) => data.deletePost, {
    successTypename: 'DeletePostSuccess',
    update: (cache, { data }, { variables }) => {
      const result = data?.deletePost
      if (!isOfType(result, 'DeletePostSuccess')) {
        return
      }

      const deletedPostId = variables?.id
      if (!deletedPostId) {
        return
      }

      cache.modify({
        fields: {
          posts(
            existingPostsConnection: Reference | StoreObject,
            { readField }: ModifierDetails,
          ) {
            if (existingPostsConnection && '__ref' in existingPostsConnection) {
              return existingPostsConnection
            }

            const edges =
              readField<PostsEdge[]>('edges', existingPostsConnection) || []

            const filteredEdges = edges.filter(
              (edge: PostsEdge) => readField('id', edge.node) !== deletedPostId,
            )

            return {
              ...existingPostsConnection,
              edges: filteredEdges,
            }
          },
        },
      })

      cache.gc()
    },
  })

export function AdminDeleteSentPostMenuItem(): JSX.Element | null {
  const { id, status } = usePostData()
  const { account } = useAccount()
  const isImpersonating = account?.isImpersonation === true
  const [deletePost, { loading }] = useAdminDeletePost()
  const [dialogOpen, setDialogOpen] = useState(false)
  const trackPostAction = useTrackActionClicked()

  const handleConfirmDelete: React.MouseEventHandler = useCallback(
    async (e) => {
      e.preventDefault()

      const { success, error } = await deletePost({ variables: { id } })
      trackPostAction('adminDeleteSentPost-1')

      if (!success) {
        toast.error(`Failed to delete post: ${error.message}`)
        return
      }

      setDialogOpen(false)
      toast.success('Sent post was deleted')
    },
    [deletePost, id, trackPostAction],
  )

  if (!isImpersonating || status !== 'sent') {
    return null
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialog.Trigger>
        <DropdownMenu.Item
          variant="critical"
          onSelect={(e): void => {
            e.preventDefault()
          }}
        >
          <TrashIcon />
          Delete Sent Post
        </DropdownMenu.Item>
      </AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete sent post?</AlertDialog.Title>
        <AlertDialog.Description>
          This will permanently delete this sent post from Buffer. This action
          cannot be undone.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              loading={loading}
              onClick={handleConfirmDelete}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
